import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Mail, Building2, Stethoscope, Send, ArrowRight, Loader2, Activity } from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, total, fresh, color, to }) {
  return (
    <Link to={to} className="bg-white rounded-2xl p-5 border border-slate-200 hover:border-[#3780E0] hover:shadow-md transition group">
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ background: color }}>
          <Icon className="w-5 h-5" />
        </div>
        {typeof fresh === "number" && fresh > 0 && (
          <span className="hc-chip !bg-[#FFF0E6] !text-[#B25A1E] !border-[#FFD6BD]">{fresh} new</span>
        )}
      </div>
      <div className="mt-4 text-3xl font-extrabold text-[#0F2A4A]">{total}</div>
      <div className="text-sm text-slate-600 mt-1">{label}</div>
      <div className="text-xs text-[#3780E0] font-semibold mt-3 inline-flex items-center gap-1 group-hover:underline">View all <ArrowRight className="w-3.5 h-3.5" /></div>
    </Link>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/stats");
        setStats(res.data);
      } catch (e) {
        toast.error("Could not load dashboard data");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-7 h-7 animate-spin text-[#3780E0]" /></div>;
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#0F2A4A]">Dashboard</h1>
          <p className="text-slate-600 text-sm">Overview of inbound activity across the HomeCure website.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <StatCard icon={Mail} label="Contact messages" total={stats?.contacts.total || 0} fresh={stats?.contacts.new} color="#3780E0" to="/admin/contacts" />
        <StatCard icon={Building2} label="Hospital inquiries" total={stats?.hospital_inquiries.total || 0} fresh={stats?.hospital_inquiries.new} color="#2563B3" to="/admin/hospitals" />
        <StatCard icon={Stethoscope} label="Personnel inquiries" total={stats?.personnel_inquiries.total || 0} fresh={stats?.personnel_inquiries.new} color="#0F4C5C" to="/admin/personnel" />
        <StatCard icon={Send} label="Newsletter subscribers" total={stats?.newsletter.total || 0} color="#3780E0" to="/admin/newsletter" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-8">
        <div className="bg-white rounded-2xl p-5 border border-slate-200">
          <div className="flex items-center gap-2 font-bold text-[#0F2A4A]"><Mail className="w-4 h-4 text-[#3780E0]" /> Latest contact messages</div>
          <ul className="mt-3 divide-y divide-slate-100">
            {(stats?.recent?.contacts || []).length === 0 && <li className="text-sm text-slate-500 py-3">No messages yet.</li>}
            {(stats?.recent?.contacts || []).map((c) => (
              <li key={c.id} className="py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm text-[#0F2A4A] truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 shrink-0">{formatDate(c.created_at)}</div>
                </div>
                <div className="text-xs text-slate-500 truncate">{c.email} — {c.subject || "No subject"}</div>
              </li>
            ))}
          </ul>
          <Link to="/admin/contacts" className="hc-link text-sm inline-flex items-center gap-1 mt-2">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200">
          <div className="flex items-center gap-2 font-bold text-[#0F2A4A]"><Building2 className="w-4 h-4 text-[#3780E0]" /> Latest hospital inquiries</div>
          <ul className="mt-3 divide-y divide-slate-100">
            {(stats?.recent?.hospitals || []).length === 0 && <li className="text-sm text-slate-500 py-3">No inquiries yet.</li>}
            {(stats?.recent?.hospitals || []).map((c) => (
              <li key={c.id} className="py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm text-[#0F2A4A] truncate">{c.hospital_name}</div>
                  <div className="text-xs text-slate-500 shrink-0">{formatDate(c.created_at)}</div>
                </div>
                <div className="text-xs text-slate-500 truncate">{c.city} — {c.contact_person}</div>
              </li>
            ))}
          </ul>
          <Link to="/admin/hospitals" className="hc-link text-sm inline-flex items-center gap-1 mt-2">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200">
          <div className="flex items-center gap-2 font-bold text-[#0F2A4A]"><Stethoscope className="w-4 h-4 text-[#3780E0]" /> Latest personnel applications</div>
          <ul className="mt-3 divide-y divide-slate-100">
            {(stats?.recent?.personnel || []).length === 0 && <li className="text-sm text-slate-500 py-3">No applications yet.</li>}
            {(stats?.recent?.personnel || []).map((c) => (
              <li key={c.id} className="py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm text-[#0F2A4A] truncate">{c.full_name}</div>
                  <div className="text-xs text-slate-500 shrink-0">{formatDate(c.created_at)}</div>
                </div>
                <div className="text-xs text-slate-500 truncate">{(c.profession || '').replace(/_/g,' ')} — {c.city}</div>
              </li>
            ))}
          </ul>
          <Link to="/admin/personnel" className="hc-link text-sm inline-flex items-center gap-1 mt-2">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </div>
    </div>
  );
}
